import React, { useState, useEffect } from "react";
import { motion } from 'framer-motion';
import ProjectsTutorial from './ProjectsTutorial';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  Card,
  Grid,
  Container,
  Chip,
  CardMedia,
  CardContent,
  Tooltip,
  Paper,
  CardActions,
  IconButton,
   Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert
} from "@mui/material";



import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import useAxiosAuth from '../hooks/useAxiosAuth';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'; 
// Define the ProjectsPage component

const ProjectsPage = ({ user, onInteraction = () => {} }) => {  

  // States for managing projects, details, and search results
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [followUpError, setFollowUpError] = useState("");
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState("");
  const [projectDetails, setProjectDetails] = useState("");
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [query, setQuery] = useState("");
  const [followUpAnswer, setFollowUpAnswer] = useState("");
  const [userQuestion, setUserQuestion] = useState("");
  const [results, setResults] = useState({ videos: [], products: [] });
  const [loading, setLoading] = useState(false);
  const [showVideos, setShowVideos] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const [projectAlias, setProjectAlias] = useState("");
  const [aliasError, setAliasError] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [showFollowUpQuestionBox, setShowFollowUpQuestionBox] = useState(false);
  //const [localError, setLocalError] = useState("");
  const [localError] = useState("");
  //const [error, setLocalError] = useState("");
  // Define theme
  const axios = useAxiosAuth();
  //console.log("ProjectsPage mounted. User:", user);
  //const { user } = useAuth();
  //console.log("URL received in ProjectsPage:",axios);

  const defaultHeaders = {
  'Content-Type': 'application/json'
};

const handleInteraction = (callback) => {
  if (!user) {
    onInteraction();
    return;
  }
  callback();
};

useEffect(() => {
  console.log("ProjectsPage received user:", user);
  if (user) {
    fetchProjects();  // No need to pass user
    fetchActiveProject();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [user]);

 

const fetchProjects = async () => {
  try {
    const response = await axios.get('/projects',{ headers: defaultHeaders });
    setProjects(response.data.projects);
    console.log("Fetched projects:", response.data.projects);
  } catch (err) {
    console.error("Failed to fetch projects:", err);
    setError("Failed to fetch projects.");
  }
};

const fetchActiveProject = async () => {
  if (!user) {
    console.error("No user is logged in.");
    return;
  }

  try {

    const response = await axios.get('/projects/active',{ headers: defaultHeaders });

    const active = response.data.active_project?.topic; // Retrieve the active project topic
    if (!active) {
      console.warn("No active project found from the backend.");
      setActiveProject(null); // Clear any existing active project in the frontend
      setError("No active project found."); // Optional: Display error message
      return;
    }

    setActiveProject(active); // Update the active project in the frontend state
    console.log("Fetched active project:", active);
  } catch (err) {
    console.error("Failed to fetch active project:", err);
    setError("Failed to fetch active project.");
  }
};




  // Fetch project details
const fetchProjectDetails = async (projectName) => {
  handleInteraction(async () => {
    try {
      setDetailsLoading(true);
      const response = await axios.get(`/projects/${encodeURIComponent(projectName)}/details`,{ headers: defaultHeaders });
      setProjectDetails(response.data.details || "No details available.");
      setShowDetails(true);
    } catch (err) {
      setError("Failed to fetch project details.");
    } finally {
      setDetailsLoading(false);
    }
  });
};


  // Handle setting active project
const handleSetActiveProject = async (projectName) => {
  handleInteraction(async () => {
    try {
      const response = await axios.post('/projects/active', { project_name: projectName }, { headers: defaultHeaders });
      
      setActiveProject(projectName);
      setResults({
        videos: response.data.results || [],
        products: response.data.products || [],
      });

      setShowVideos(false);
      setShowProducts(false);

      console.log("Active project set successfully.");
    } catch (err) {
      console.error("Failed to set active project:", err);
      setError("Failed to set active project.");
    }
  });
};

const handleFollowUpQuestion = async () => {
  handleInteraction(async () => {
    if (!activeProject) {
      setFollowUpError("No active project to ask a follow-up question.");
      return;
    }
    
    if (!userQuestion.trim()) {
      setFollowUpError("Please enter your follow-up question.");
      return;
    }
    
    // Add client-side character validation
    if (userQuestion.length > 500) {
      setFollowUpError("Your question must be less than 500 characters.");
      return;
    }
    
    try {
      setLoading(true);
      setFollowUpError("");
      console.log("Sending follow-up question:", userQuestion);
      const response = await axios.post('/projects/follow-up-question', 
        { project_question: userQuestion },
        { headers: defaultHeaders }
      );
      console.log("Follow-up response:", response.data);
      setFollowUpAnswer(response.data.answer || "No answer available.");
      setUserQuestion("");
      setError("");
    } catch (err) {
      console.error("Follow-up question error:", err);
      // Extract the specific error message from the API response if available
      if (err.response && err.response.data && err.response.data.detail) {
        setFollowUpError(err.response.data.detail);
      } else {
        setFollowUpError("Failed to get an answer to your follow-up question.");
      }
    } finally {
      setLoading(false);
    }
  });
};

 

const handleSearch = async () => {
  handleInteraction(async () => {
    if (!query.trim()) {
      return;
    }
    
    // Add client-side character validation
    if (query.length > 700) {
      setError("Your search query must be less than 700 characters.");
      return;
    }
    
    setLoading(true);
    setError("");
    setResults({ videos: [], products: [] });
    setSearchAttempted(true);
    
    try {
      const response = await axios.post('/search', 
        { q: query, project_alias: projectAlias }
      );
      console.log("Search response:", response.data);
      
      setResults({
        videos: response.data.results || [],
        products: response.data.products || [],
      });
      
      // Show notification after search completes successfully
      if (response.data.results?.length > 0 || response.data.products?.length > 0) {
        setShowNotification(true);
        
        // Hide notification after 10 seconds
        setTimeout(() => {
          setShowNotification(false);
        }, 10000);
      }
      
      await fetchProjects();
      
      //////NEW
      if (response.data.new_project_name) {
        setActiveProject(response.data.new_project_name);
      }
    } catch (err) {
      console.error("Search error:", err);
      
      // Check for specific error related to no search results
      const errorMessage = err.response?.data?.detail || "";
      
      // Only display errors that aren't about the "str object has no attribute 'keys'" issue
      if (!errorMessage.includes("'str' object has no attribute 'keys'")) {
        // Extract the specific error message from the API response if available
        if (err.response && err.response.data && err.response.data.detail) {
          setError(err.response.data.detail);
        } else {
          setError("Failed to complete search. Please try again.");
        }
        
        // Auto-hide error after 10 seconds
        setTimeout(() => {
          setError("");
        }, 10000);
      }
      
      // When we get this specific error, treat it as "no results found"
      if (errorMessage.includes("'str' object has no attribute 'keys'")) {
        setResults({ videos: [], products: [] });
        setSearchAttempted(true);
      }
    } finally {
      setLoading(false);
    }
  });
};


const addDetailsToProject = async () => {
  handleInteraction(async () => {
    if (!activeProject) {
      setError("No active project to add details.");
      return;
    }
    try {
      setDetailsLoading(true);
      
      await axios.post(`/projects/next-step`,{ headers: defaultHeaders })
      console.log("Next step updated successfully.");
      setShowDetails(false);
      setError("");
    } catch (err) {
      console.error("Failed to add details to project:", err);
      setError("Failed to add details to project.");
    } finally {
      setDetailsLoading(false);
    }
  });
};

const handleDeleteProject = async (projectName) => {
  handleInteraction(async () => {
    try {
      await axios.delete(`/projects/${encodeURIComponent(projectName)}`);
      
      // If the deleted project was active, clear the active project
      if (activeProject === projectName) {
        setActiveProject(null);
        setResults({ videos: [], products: [] });
      }
      
      // Refresh the projects list
      fetchProjects();
      
    } catch (err) {
      setError(`Failed to delete project: ${err.response?.data?.detail || err.message}`);
    }
  });
};

const toggleFollowUpQuestionBox = () => {
  setShowFollowUpQuestionBox(prev => !prev);
  // Clear any previous answers when toggling
  if (!showFollowUpQuestionBox) {
    setFollowUpAnswer("");
    setFollowUpError("");
    setUserQuestion("");
  }
};

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
    {/* Delete Confirmation Dialog */}
<Dialog
  open={deleteConfirmOpen}
  onClose={() => setDeleteConfirmOpen(false)}
>
  <DialogTitle>Confirm Delete</DialogTitle>
  <DialogContent>
    <DialogContentText>
      Are you sure you want to delete the project "{projectToDelete}"? This action cannot be undone.
    </DialogContentText>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
    <Button 
      onClick={() => {
        handleDeleteProject(projectToDelete);
        setDeleteConfirmOpen(false);
      }} 
      color="error"
    >
      Delete
    </Button>
  </DialogActions>
</Dialog>
    {/* Add the error Alert component here, right after the Dialog */}
    {error && (
      <Alert 
        severity="error" 
        sx={{ 
          mb: 3, 
          borderRadius: 2,
          width: "100%",
          maxWidth: "lg",
          mx: "auto"
        }}
        onClose={() => setError("")}
      >
        {error}
      </Alert>
    )}
      <Container maxWidth="lg">
        {/* Back to DIYMind Home Link */}
  <Box sx={{ display: 'flex', mb: 2, mt: 1 }}>
    <Button
      startIcon={<ArrowBackIosIcon />}
      variant="text"
      onClick={() => navigate('/')}
      sx={{
        color: '#1e3c72',
        fontWeight: 500,
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        }
      }}
    >
      Back to DIYMind Home
    </Button>
  </Box>
        {/* Header Section */}
       <Box 
  component={motion.div}
  initial={{ opacity: 0, y: -20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.5 }}
  sx={{ mb: 6 }}
>
  <Typography 
    variant="h1" 
    gutterBottom 
    sx={{ 
      fontSize: { xs: '2.5rem', md: '3.5rem' },
      fontWeight: 'bold',
      mb: 2,
      background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
      backgroundClip: 'text',
      textFillColor: 'transparent'
    }}
  >
            Find Your Next Project
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Search for the best DIY tutorials and products.
          </Typography>
          
          {/* Project Creation Form */}
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2, mb: 4 }}>
            {/* Project Name Input */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Project Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="e.g., Kitchen Modernization"
                value={projectAlias}
                onChange={(e) => handleInteraction(() => {
                  setProjectAlias(e.target.value);
                  setAliasError(false);
                })}
                error={aliasError}
                helperText={aliasError ? "Project name is required" : ""}
              />
            </Box>
            
{/* Search Input */}
<Box sx={{ mb: 3 }}>
  <Typography variant="subtitle1" gutterBottom>
    What are you looking for? <span style={{ color: "red" }}>*</span>
    <Tooltip title="Describe your project or what you want to build">
      <IconButton size="small" sx={{ ml: 1 }}>
        <HelpOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Typography>
  <TextField
    fullWidth
    variant="outlined"
    placeholder="Enter your search query"
    value={query}
    onChange={(e) => {
      const value = e.target.value;
      handleInteraction(() => {
        setQuery(value);
        if (error) setError("");
      });
    }}
    multiline
    rows={2}
    error={query.length > 700}
    helperText={
      query.length > 700 
        ? `Character count: ${query.length}/700 (Exceeds limit)`
        : `Character count: ${query.length}/700`
    }
  />
</Box>

<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
  <Button
    variant="contained"
    color="primary"
    startIcon={<SearchIcon />}
    onClick={() => handleInteraction(handleSearch)}
    disabled={loading || query.length > 700 || !query.trim()}
    sx={{ px: 4 }}
  >
    {loading ? "Searching..." : "Search"}
  </Button>
</Box>
          </Paper>
        </Box>
        {/* Error Message Alert */}
{error && (
  <Alert 
    severity="error" 
    sx={{ 
      mt: 2, 
      mb: 2, 
      display: 'flex', 
      alignItems: 'center',
      borderRadius: 2,
      backgroundColor: '#e3f2fd', // Light blue background
      color: '#0d47a1',  // Dark blue text
      border: '1px solid #bbdefb', // Light blue border
      '& .MuiAlert-icon': {
        color: '#1976d2' // Blue icon color
      }
    }}
  >
    <Typography variant="body2">
      {error}
    </Typography>
  </Alert>
)}
{showNotification && (results.videos?.length > 0 || results.products?.length > 0) && (
  <Alert 
    severity="info" 
    sx={{ 
      mt: 2, 
      mb: 2, 
      display: 'flex', 
      alignItems: 'center',
      borderRadius: 2
    }}
  >
    <Typography variant="body2">
      Search complete! Scroll down to see video and product results at the bottom of the page.
    </Typography>
  </Alert>
)}
{/* No Results Message */}
{searchAttempted && !loading && (!results.videos?.length && !results.products?.length) && (
  <Alert
    severity="info"
    sx={{
      mt: 2,
      mb: 2,
      display: 'flex',
      alignItems: 'center',
      borderRadius: 2,
      backgroundColor: '#e3f2fd', // Light blue background
      color: '#0d47a1',  // Dark blue text
      border: '1px solid #bbdefb', // Light blue border
      '& .MuiAlert-icon': {
        color: '#1976d2' // Blue icon color
      }
    }}
  >
    <Typography variant="body2">
      No results found. Try a different query.
    </Typography>
  </Alert>
)}
{/* Follow-Up Question Section */}
<Box sx={{ mb: 4 }}>
  {activeProject && (
    <Button
      variant="outlined"
      startIcon={showFollowUpQuestionBox ? <ExpandMoreIcon /> : <SearchIcon />}
      onClick={toggleFollowUpQuestionBox}
      sx={{ 
        mb: 2,
        color: '#3f51b5',
        bgcolor: 'white',
        borderRadius: 2,
        fontWeight: 500,
        borderColor: '#3f51b5',
        '&:hover': {
          bgcolor: 'rgba(25, 118, 210, 0.04)',
          color: '#3f51b5',
        }
      }}
    >
      {showFollowUpQuestionBox ? "Hide Follow-Up Question" : "Ask Follow-Up Question"}
    </Button>
  )}
  
  {showFollowUpQuestionBox && activeProject && (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 3, 
        borderRadius: 2, 
        mb: 4,
        animation: 'fadeIn 0.3s ease-in-out',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(-10px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' }
        }
      }}
    >
      {followUpError && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setFollowUpError("")}
        >
          {followUpError}
        </Alert>
      )}
      <Typography variant="h5" gutterBottom>Ask a Follow-Up Question</Typography>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Enter your follow-up question here"
        value={userQuestion}
        onChange={(e) => {
          const value = e.target.value;
          handleInteraction(() => {
            setUserQuestion(value);
            if (followUpError) setFollowUpError("");
          });
        }}
        error={userQuestion.length > 500}
        helperText={
          userQuestion.length > 500 
            ? `Character count: ${userQuestion.length}/500 (Exceeds limit)`
            : `Character count: ${userQuestion.length}/500`
        }
        sx={{ mb: 2 }}
      />
      
      <Button
        variant="outlined"
        onClick={() => handleInteraction(handleFollowUpQuestion)}
        disabled={!activeProject || !userQuestion.trim() || loading || userQuestion.length > 500}
        sx={{ 
          mb: 2, 
          color: '#3f51b5',
          bgcolor: 'white',
          borderRadius: 2,
          fontWeight: 500,
          borderColor: '#3f51b5',
          '&:hover': {
            bgcolor: 'rgba(25, 118, 210, 0.04)',
            color: '#3f51b5',
          },
          '&.Mui-disabled': {
            color: '#3f51b5',
            opacity: 0.7,
            borderColor: '#3f51b5'
          }
        }}
      >
        Submit Question
      </Button>
      
      {followUpAnswer && (
        <Box sx={{ mt: 3, p: 3, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle1" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
            Answer:
          </Typography>
          <Typography variant="body1">{followUpAnswer}</Typography>
        </Box>
      )}
      
      {localError && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {localError}
        </Typography>
      )}
    </Paper>
  )}
</Box>

        {/* Active Project and Results Section */}
        <Box sx={{ mt: 4 }}>
          {/* Projects Dropdown */}
          <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom>Your Projects</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
           <Select
  value={activeProject || ""}
  onChange={(e) => handleInteraction(() => handleSetActiveProject(e.target.value))}
  sx={{ flexGrow: 1, minWidth: '200px' }}
  displayEmpty
>
  <MenuItem value="" disabled>Select a project</MenuItem>
  {projects.map((project) => (
    <MenuItem 
      key={project.name}
      value={project.name}
      sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <span>{project.name}</span>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation(); // Prevent select from opening
          setProjectToDelete(project.name);
          setDeleteConfirmOpen(true);
        }}
        sx={{ ml: 2 }}
      >
        <DeleteIcon color="error" />
      </IconButton>
    </MenuItem>
  ))}
</Select>
              
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleInteraction(() => {
                  if (showDetails) {
                    setShowDetails(false);
                  } else {
                    fetchProjectDetails(activeProject);
                  }
                })}
                disabled={!activeProject}
              >
                {showDetails ? "Hide Details" : "View Details"}
              </Button>
              
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleInteraction(() => {
                  setActiveProject("");
                  setProjectAlias("");
                  setQuery("");
                })}
              >
                New Project
              </Button>
            </Box>
          </Paper>
          {/* Project Details Display */}
{showDetails && projectDetails && (
  <Paper 
    component={motion.div}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    elevation={3} 
    sx={{ 
      p: 3, 
      mb: 4, 
      borderRadius: 2,
      background: 'linear-gradient(to bottom, white, #f9f9f9)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
    }}
  >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                  Project Details
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DesignServicesIcon />}
                  onClick={() => handleInteraction(() => addDetailsToProject(activeProject))}
                  disabled={detailsLoading}
                >
                  {detailsLoading ? "Processing..." : "Add Details"}
                </Button>
              </Box>
              
              {detailsLoading ? (
                <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
                  Loading project details...
                </Typography>
              ) : (
                <Box>
                  {projectDetails.split("\n\n").map((paragraph, index) => (
                    <Card key={index} sx={{ mb: 2, overflow: 'visible' }}>
                      <CardContent>
                        <Typography
                          variant="subtitle1"
                          sx={{ color: "#3f51b5", fontWeight: 'bold', mb: 1 }}
                        >
                          Step {index + 1}
                        </Typography>
                        <Typography variant="body1">{paragraph}</Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Paper>
          )}


  {/* Video Results */}
{(activeProject || searchAttempted) && (results.videos && results.videos.length > 0) && (
  <Accordion 
    expanded={showVideos} 
    onChange={() => handleInteraction(() => setShowVideos(!showVideos))}
    sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <PlayCircleOutlineIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">
          Videos {results.videos?.length > 0 && `(${results.videos.length})`}
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails>
      <Grid container spacing={3}>
        {results.videos.map((video, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              component={motion.div}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '12px',
                overflow: 'hidden',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                },
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="160"
                  image={video.thumbnail_url || "/api/placeholder/300/180"}
                  alt={video.title || "Video Thumbnail"}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    bgcolor: 'rgba(0, 0, 0, 0.2)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s',
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                >
                  <PlayCircleOutlineIcon
                    sx={{
                      color: 'white',
                      fontSize: '3rem',
                      filter: 'drop-shadow(0px 0px 8px rgba(0,0,0,0.5))',
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ p: 2, bgcolor: '#1e3c72', color: 'white' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1.3 }}>
                  {video.title || "Video Title"}
                </Typography>
              </Box>
              <CardContent sx={{ flexGrow: 1, pt: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Chip
                    icon={<PlayCircleOutlineIcon />}
                    label="Tutorial"
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 'medium' }}
                  />
                </Box>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  color="primary"
                  href={video.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<PlayCircleOutlineIcon />}
                  fullWidth
                >
                  Watch Video
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </AccordionDetails>
  </Accordion>
)}
{/* Product Results */}
{(results.products && results.products.length > 0) && (
  <Accordion 
    expanded={showProducts} 
    onChange={() => handleInteraction(() => setShowProducts(!showProducts))}
    sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <ShoppingBagOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">
          Products {results.products?.length > 0 && `(${results.products.length})`}
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails>
      <Grid container spacing={2}>
        {results.products.map((product, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              component={motion.div}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '12px',
                overflow: 'hidden',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                },
              }}
            >
              <Box sx={{ p: 2, bgcolor: '#1e3c72', color: 'white' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                  {product.name}
                </Typography>
              </Box>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ mb: 2 }}>
                  <Chip
                    icon={<ShoppingBagOutlinedIcon />}
                    label={product.category || "Product"}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 'medium' }}
                  />
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {product.description}
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  color="primary"
                  href={product.product_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  fullWidth
                >
                  View Product
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </AccordionDetails>
  </Accordion>
)}

          {/* No Results Message */}
          {searchAttempted && !loading && (!results.videos?.length && !results.products?.length) && (
            <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
              No results found. Try a different query.
            </Typography>
          )}
        </Box>
      </Container>
      <ProjectsTutorial />
    </Box>
  );
};

export default ProjectsPage;
